// utils/errorLogger/handlers.ts - utils/errorLogger.ts split (sub-module handlers)


import { isNoiseError } from './rateLimit.js';
import { logError } from './log.js';
import { logErrorWithPayload } from './log.js';


/**
 * Create a try-catch wrapper that automatically logs errors
 */
export const withErrorLogging = <T extends (...args: any[]) => Promise<any>>(
    fn: T,
    context: string
): T => {
    return (async (...args: Parameters<T>) => {
        try {
            return await fn(...args);
        } catch (error) {
            await logError(error as Error, context);
            throw error;
        }
    }) as T;
};



/**
 * Global error handler - can be used for window.onerror
 */
export const globalErrorHandler = (
    message: string | Event,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error
): void => {
    const errorMessage = error?.message || String(message);
    if (isNoiseError(errorMessage)) return;

    logError(
        error || new Error(errorMessage),
        'GlobalErrorHandler',
        {
            source,
            lineno,
            colno
        }
    );
};



/**
 * Promise rejection handler - can be used for window.onunhandledrejection
 */
export const unhandledRejectionHandler = (event: PromiseRejectionEvent): void => {
    const error = event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));

    logError(error, 'UnhandledRejection');
};



/**
 * Setup global error handlers
 */
export const setupGlobalErrorHandlers = (): void => {
    if (typeof window !== 'undefined') {
        window.onerror = globalErrorHandler;
        window.onunhandledrejection = unhandledRejectionHandler;

        if (import.meta.env.DEV) {
            console.log('[ErrorLogger] Global error handlers installed');
        }
    }
};



export default {
    logError,
    logErrorWithPayload,
    withErrorLogging,
    setupGlobalErrorHandlers
};
